import { useState } from 'react';

import { Flex, Skeleton } from '@chakra-ui/react';
import { Card } from '@mesh.ai/rabbit';
import { useQuery } from 'components/hooks/useQuery';
import { useToast } from 'components/hooks/useToast';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { openCreateOKRPanel } from 'reducers/createObjectivePanelSlice';
import { useProfilePage } from 'screens/Profile/hooks/dataHooks';
import { openCreateOneOnOneDrawer } from 'screens/oneOnOneRabbit/creationDrawer/slice/CreateOneOnOneDrawerSlice';
import { okrsPageSelector } from 'selectors/okrsPage';

import { OKR_TYPES } from 'constants/ProjectConstants';

import { CompetencyReviewActionPlanBottomSheet } from './CompetencyReviewActionPlanBottomSheet';
import { DiscussOneOnOneSheet } from './DiscussOneOnOne';
import ExplainWhyBottomSheet from './ExplainWhyBottomSheet';
import Header from './Header';
import { ActionList } from './components/ActionList';
import { DeleteActionItemModal } from './components/DeleteActionItemModal';
import { REVIEW_ACTIONS } from './constant';
import { deleteActionPlanItem, useGetPOAList } from './hooks/dataHooks';
import type { ActionPlanList } from './types';

const ReviewActionPlan = () => {
  const { revieweeName, revieweeId, reviewCycle } = useQuery();
  const { data: employeeDetails } = useProfilePage({ employeeId: revieweeId as string });
  const {
    data: actionPlanListResponse,
    isLoading: isLoadingActionPlanList,
    refetch: refetchActionPlanList,
  } = useGetPOAList({ reviewCycle, revieweeId });
  const actionPlanList = actionPlanListResponse as ActionPlanList;
  const { selectedTimelines } = useSelector(okrsPageSelector);
  const toast = useToast();

  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const [selectedSuggestion, setSelectedSuggestion] = useState<keyof typeof REVIEW_ACTIONS | null>(null);
  const [selectedActionItemId, setSelectedActionItemId] = useState<number | null>(null);
  const [selectedActionItem, setSelectedActionItem] = useState(null);

  const handleActionSelect = (actionType: keyof typeof REVIEW_ACTIONS, actionItemId: number) => {
    setSelectedActionItemId(actionItemId);

    switch (actionType) {
      case REVIEW_ACTIONS.GOAL: {
        if (!employeeDetails) {
          toast({
            title: 'An unknown error has occurred, please try again',
            status: 'error',
          });
          return;
        }

        const title =
          actionPlanList.reviewActionPlanItems.find((item) => item.id === actionItemId)?.actionItemText ?? '';

        dispatch(
          // @ts-expect-error ts issue
          openCreateOKRPanel({
            selectedObjective: null,
            selectedTimeBoundPeriod: selectedTimelines?.[0],
            okrPanelType: OKR_TYPES.KEY_RESULT,
            okrPanelPayload: {
              from: 'REVIEW_ACTION_PLAN',
              actionPlanId: actionItemId,
              reviewCycleId: reviewCycle,
              title,
            },
            ownerEmployee: employeeDetails,
            onKeyResultCreation: refetchActionPlanList,
            onObjectiveCreation: refetchActionPlanList,
            history,
            location,
          })
        );

        break;
      }

      case REVIEW_ACTIONS.ONE_ON_ONE: {
        if (!actionPlanList) {
          toast({
            title: 'An unknown error has occurred, please try again',
            status: 'error',
          });
          return;
        }

        // check if 1:1 is already scheduled, if not open create 1:1 sheet with selected employee auto filled
        const isOneOnOneAlreadyScheduled = actionPlanList.oneOnOneDetails.oneOnOneExists;

        if (isOneOnOneAlreadyScheduled) {
          // show scheduled one on one details sheet
          setSelectedSuggestion(REVIEW_ACTIONS.ONE_ON_ONE);
        } else {
          if (!employeeDetails) {
            toast({
              title: 'An unknown error has occurred, please try again',
              status: 'error',
            });
            return;
          }

          const employeeId = Number(employeeDetails.id);

          // create mini profile from profile data
          const employeeMiniProfile = {
            id: employeeId,
            displayName: employeeDetails.displayName,
            profilePhoto: employeeDetails.profilePhotoSrc,
            designation: employeeDetails.designation,
            ownerType: employeeDetails.ownerType,
            employeeFunction: employeeDetails.employeeFunction,
            email: employeeDetails.email,
          };

          dispatch(
            // @ts-expect-error ignore
            openCreateOneOnOneDrawer({
              excludedParticipantIds: [],
              employeeId: revieweeId as string,
              employeeMiniProfile,
              onCreateOneOnOne: refetchActionPlanList,
            })
          );
        }

        break;
      }

      case REVIEW_ACTIONS.COMPETENCY_IDP: {
        setSelectedSuggestion(REVIEW_ACTIONS.COMPETENCY_IDP);

        break;
      }
      case REVIEW_ACTIONS.EXPLAIN_WHY: {
        const selectedActionPlanItem = actionPlanList?.reviewActionPlanItems.find((item) => item.id === actionItemId);
        setSelectedActionItem(selectedActionPlanItem);
        setSelectedSuggestion(REVIEW_ACTIONS.EXPLAIN_WHY);
        break;
      }

      case REVIEW_ACTIONS.DELETE: {
        setSelectedSuggestion(REVIEW_ACTIONS.DELETE);
        break;
      }

      default: {
        break;
      }
    }
  };

  const deleteActionItem = async () => {
    setSelectedSuggestion(null);

    try {
      await deleteActionPlanItem({ reviewCycle: reviewCycle as string, actionPlanId: selectedActionItemId });
      refetchActionPlanList();
      toast({
        title: 'Action item deleted successfully',
        status: 'success',
      });
    } catch (err) {
      console.error('Error deleting plan of action : ', err);
      throw err;
    }
  };

  return (
    <>
      <Header revieweeName={revieweeName} />
      <Card style={{ padding: '12px' }}>
        {isLoadingActionPlanList && (
          <Flex flexDir="column" gap="12px" w="100%">
            <Skeleton h="85px" w="100%" />
            <Skeleton h="85px" w="100%" />
            <Skeleton h="85px" w="100%" />
            <Skeleton h="85px" w="100%" />
            <Skeleton h="85px" w="100%" />
            <Skeleton h="40px" w="150px" />
          </Flex>
        )}
        {!isLoadingActionPlanList && (
          <ActionList
            reviewActionPlanItems={actionPlanList?.reviewActionPlanItems}
            reviewCycle={reviewCycle as string}
            revieweeId={revieweeId as string}
            revieweeName={revieweeName as string}
            onActionSelect={handleActionSelect}
            refetchActionPlanList={refetchActionPlanList}
          />
        )}
      </Card>
      {selectedSuggestion === REVIEW_ACTIONS.EXPLAIN_WHY && (
        <ExplainWhyBottomSheet
          onClose={() => setSelectedSuggestion(null)}
          revieweeName={revieweeName}
          actionItem={selectedActionItem}
        />
      )}
      {selectedSuggestion === REVIEW_ACTIONS.ONE_ON_ONE && (
        <DiscussOneOnOneSheet
          onClose={() => setSelectedSuggestion(null)}
          revieweeName={revieweeName as string}
          reviewCycle={reviewCycle as string}
          masterId={actionPlanList?.oneOnOneDetails.masterId}
          instanceId={actionPlanList?.oneOnOneDetails.instanceId}
          actionPlanId={selectedActionItemId}
          onAddAgenda={refetchActionPlanList}
        />
      )}
      {selectedSuggestion === REVIEW_ACTIONS.COMPETENCY_IDP && (
        <CompetencyReviewActionPlanBottomSheet
          onClose={() => setSelectedSuggestion(null)}
          employeeId={employeeDetails?.id as string}
          reviewCycleId={reviewCycle as string}
          actionPlanId={selectedActionItemId}
          toIds={[
            {
              uuid: employeeDetails?.id as string,
              ownerType: 'OWNER',
              metadata: {
                displayName: employeeDetails?.displayName as string,
              },
              profile: {
                profilePhoto: employeeDetails?.profilePhotoSrc as string,
              },
            },
          ]}
          onAddInitiative={refetchActionPlanList}
        />
      )}
      {selectedSuggestion === REVIEW_ACTIONS.DELETE && (
        <DeleteActionItemModal onClose={() => setSelectedSuggestion(null)} onConfirm={deleteActionItem} />
      )}
    </>
  );
};

export default ReviewActionPlan;
